import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import { fmtMoney } from "../lib/exports";
import { Lock, Unlock, AlertTriangle } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function CierreDiario() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const puedeEditar = canEdit(user, "cierre_diario");
  const [fecha, setFecha] = useState(today());
  const [sucursalId, setSucursalId] = useState("");
  const [sucursales, setSucursales] = useState([]);
  const [cierre, setCierre] = useState(null);
  const [efectivo, setEfectivo] = useState("");
  const [tarjeta, setTarjeta] = useState("");
  const [uber, setUber] = useState("");
  const [rappi, setRappi] = useState("");
  const [observaciones, setObservaciones] = useState("");

  useEffect(() => { api.get("/sucursales").then((r) => { setSucursales(r.data); if (r.data[0]) setSucursalId(user?.sucursal_default || r.data[0].id); }); }, [user]);

  const cargar = async () => {
    if (!sucursalId) return;
    let data = null;
    try {
      const res = await api.get("/cierres/dia", { params: { fecha, sucursal_id: sucursalId } });
      data = res.data;
    } catch (e) {
      toast.error(formatErr(e));
      return;
    }
    setCierre(data);
    if (data) {
      setEfectivo(data.efectivo);
      setTarjeta(data.tarjeta);
      setUber(data.uber);
      setRappi(data.rappi);
      setObservaciones(data.observaciones || "");
    } else {
      setEfectivo("");
      setTarjeta("");
      setUber("");
      setRappi("");
      setObservaciones("");
    }
  };
  useEffect(() => { cargar(); }, [fecha, sucursalId]);

  const total = (parseFloat(efectivo) || 0) + (parseFloat(tarjeta) || 0) + (parseFloat(uber) || 0) + (parseFloat(rappi) || 0);
  const diferencia = cierre ? total - (cierre.ventas_sistema || 0) : 0;
  const yaCerrado = cierre?.status === "cerrado";

  const cerrar = async () => {
    if (yaCerrado && !isAdmin) return toast.error("El día ya está cerrado");
    if (yaCerrado && !window.confirm("Este día ya está cerrado. ¿Sobrescribir?")) return;
    try {
      const { data } = await api.post("/cierres", {
        fecha, sucursal_id: sucursalId,
        efectivo: parseFloat(efectivo) || 0, tarjeta: parseFloat(tarjeta) || 0,
        uber: parseFloat(uber) || 0, rappi: parseFloat(rappi) || 0, observaciones,
      });
      toast.success("Día cerrado correctamente");
      setCierre(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const reabrir = async () => {
    if (!cierre?.id) return;
    if (!window.confirm("¿Reabrir el día? Solo admin puede modificar.")) return;
    try { await api.post(`/cierres/${cierre.id}/reabrir`); toast.success("Reabierto"); cargar(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Operación</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Cierre Diario</h1>
        <p className="text-sm text-slate-500 mt-1">Corte de caja y conciliación con el sistema.</p>
      </div>

      <Card className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Fecha</Label>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="mt-1.5 bg-blue-50 border-blue-200" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Sucursal</Label>
          <Select value={sucursalId} onValueChange={setSucursalId}>
            <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200"><SelectValue /></SelectTrigger>
            <SelectContent>{sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      {yaCerrado && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-4 flex items-center gap-3">
          <Lock className="w-5 h-5" />
          <div className="flex-1">
            <div className="font-semibold text-sm">Día cerrado</div>
            <div className="text-xs">Cerrado por {cierre.cerrado_por} el {cierre.cerrado_at?.slice(0, 19).replace("T", " ")}</div>
          </div>
          {isAdmin && <Button variant="outline" size="sm" onClick={reabrir} data-testid="cierre-reabrir"><Unlock className="w-4 h-4 mr-2" />Reabrir</Button>}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 space-y-4">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Corte de caja</div>
          <CashField label="Efectivo" value={efectivo} onChange={setEfectivo} testid="cierre-efectivo" />
          <CashField label="Tarjeta" value={tarjeta} onChange={setTarjeta} testid="cierre-tarjeta" />
          <CashField label="Uber Eats" value={uber} onChange={setUber} testid="cierre-uber" />
          <CashField label="Rappi" value={rappi} onChange={setRappi} testid="cierre-rappi" />
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Observaciones</Label>
            <Textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} className="mt-1.5 bg-blue-50 border-blue-200" rows={3} />
          </div>
        </Card>

        <Card className="p-5 bg-slate-900 text-white space-y-3">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-400">Conciliación</div>
          <Row label="Efectivo" value={fmtMoney(efectivo)} />
          <Row label="Tarjeta" value={fmtMoney(tarjeta)} />
          <Row label="Uber Eats" value={fmtMoney(uber)} />
          <Row label="Rappi" value={fmtMoney(rappi)} />
          <div className="border-t border-slate-700 pt-3">
            <Row label="TOTAL CORTE" value={fmtMoney(total)} bold />
            <Row label="Ventas Sistema" value={fmtMoney(cierre?.ventas_sistema || 0)} />
            <div className="border-t border-slate-700 pt-3 mt-3">
              <Row
                label="Diferencia"
                value={fmtMoney(diferencia)}
                bold
                positive={Math.abs(diferencia) < 0.01 ? undefined : diferencia >= 0}
              />
              {Math.abs(diferencia) > 0.01 && cierre && (
                <div className="text-xs text-amber-400 mt-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {diferencia > 0 ? "Sobrante en caja" : "Faltante en caja"}
                </div>
              )}
            </div>
          </div>

          <Button
            onClick={cerrar}
            disabled={(yaCerrado && !isAdmin) || !puedeEditar}
            data-testid="cierre-cerrar"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 mt-4"
          >
            <Lock className="w-4 h-4 mr-2" /> {!puedeEditar ? "Solo lectura" : yaCerrado ? (isAdmin ? "Actualizar Cierre" : "Día Cerrado") : "CERRAR DÍA"}
          </Button>
        </Card>
      </div>
    </div>
  );
}

const CashField = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <Input
      type="number" min="0" step="0.01" inputMode="decimal"
      value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1.5 bg-blue-50 border-blue-200 text-blue-900 font-mono"
    />
  </div>
);
const Row = ({ label, value, bold, positive }) => (
  <div className="flex justify-between font-mono text-sm">
    <span className={`text-slate-300 ${bold ? "font-semibold text-white" : ""}`}>{label}</span>
    <span className={`${bold ? "font-bold text-lg" : ""} ${positive === true ? "text-emerald-400" : positive === false ? "text-red-400" : "text-white"}`}>{value}</span>
  </div>
);
